import { motion } from 'framer-motion';
import { Phone, CheckCircle, Monitor, Shield, Clock, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FAQ } from '../components/FAQ';
import { Contact } from '../components/Contact';

const WHATSAPP_NUMBER = "5521980277322";

const services = [
  "Formatação Windows 10/11",
  "Instalação de programas",
  "Remoção de vírus e malware",
  "Upgrade de HD para SSD",
  "Aumento de memória RAM",
  "Limpeza interna e troca de pasta térmica",
  "Reparo de fonte e placa-mãe",
  "Montagem de PC personalizado",
  "Configuração de rede e internet",
  "Backup e recuperação de dados",
  "Instalação de placa de vídeo",
  "Suporte técnico remoto",
];

const problems = [
  { title: "Computador não liga", desc: "Diagnóstico completo de fonte, placa-mãe e componentes." },
  { title: "PC muito lento", desc: "Otimização de sistema, upgrade SSD e limpeza de arquivos." },
  { title: "Vírus e malware", desc: "Remoção completa de ameaças e instalação de antivírus." },
  { title: "Tela azul (BSOD)", desc: "Análise de erro e reparo de software/hardware." },
  { title: "Sem internet", desc: "Configuração de rede, drivers e placa de rede." },
  { title: "Superaquecimento", desc: "Limpeza, troca de cooler e pasta térmica." },
];

const ComputadorPage = () => {
  return (
    <div data-testid="computador-page">
      {/* Hero Section */}
      <section className="hero-bg min-h-[60vh] flex items-center pt-32 md:pt-40 pb-16">
        <div className="hero-content max-w-7xl mx-auto px-6 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <Link to="/" className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest hover:underline mb-4 inline-block">
              ← Voltar para Início
            </Link>
            
            <div className="inline-block mb-6">
              <span className="text-[#00F0FF] text-sm font-mono uppercase tracking-widest border border-[#00F0FF]/30 px-4 py-2 bg-[#00F0FF]/5">
                [ Assistência Técnica Computador ]
              </span>
            </div>
            
            <h1 className="font-orbitron text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Reparo de <span className="text-[#00F0FF] neon-text">Computador</span>
              <br />Desktop e PC
            </h1>
            
            <p className="text-[#94A3B8] text-lg md:text-xl font-space leading-relaxed mb-8">
              Técnico de informática especializado em manutenção de computadores. 
              Formatação, remoção de vírus, upgrade e suporte técnico na Barra da Tijuca.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <motion.a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Preciso de assistência técnica para meu computador.`}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="whatsapp-btn justify-center text-base"
                data-testid="computador-whatsapp-btn"
              >
                <Phone size={20} />
                Orçamento pelo WhatsApp
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Problems */}
      <section className="section-dark py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Problemas <span className="text-[#00F0FF]">Comuns</span> que Resolvemos
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((problem, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="cyber-card p-6"
              >
                <h3 className="font-rajdhani font-bold text-white text-lg mb-2">{problem.title}</h3>
                <p className="text-sm text-[#94A3B8] font-space">{problem.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-darker py-20 grid-bg">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Services */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white mb-8">
                Serviços de <span className="text-[#00F0FF]">Manutenção</span>
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle size={18} className="text-[#00F0FF] flex-shrink-0" />
                    <span className="text-sm text-[#94A3B8] font-space">{service}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* CTA Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex items-center"
            >
              <div className="cyber-card p-8 w-full border-[#00F0FF]/30">
                <div className="w-20 h-20 mx-auto mb-6 border border-[#00F0FF]/30 flex items-center justify-center bg-[#00F0FF]/5">
                  <Monitor size={40} className="text-[#00F0FF]" />
                </div>
                <h3 className="font-orbitron text-xl font-bold text-white text-center mb-4">
                  Atendimento <span className="text-[#00F0FF]">Urgente</span>
                </h3>
                <p className="text-[#94A3B8] font-space text-center mb-6">
                  Precisa de um técnico de informática urgente? 
                  Atendemos com agilidade na Barra da Tijuca e região.
                </p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Preciso de um técnico de informática URGENTE.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cyber-btn w-full text-center block"
                  data-testid="urgente-cta-btn"
                >
                  Atendimento Urgente
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-dark py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-orbitron text-2xl md:text-3xl font-bold text-white text-center mb-12">
            Por que escolher a <span className="text-[#00F0FF]">Vaz Tech</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: "Garantia Total", desc: "Todos os serviços com garantia de 30 a 90 dias." },
              { icon: Clock, title: "Atendimento Rápido", desc: "Diagnóstico e orçamento no mesmo dia." },
              { icon: Award, title: "Técnicos Experientes", desc: "Mais de 10 anos de experiência em TI." },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="cyber-card p-8 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-[#00F0FF]/30 flex items-center justify-center bg-[#00F0FF]/5">
                  <item.icon size={28} className="text-[#00F0FF]" />
                </div>
                <h3 className="font-rajdhani font-bold text-white text-xl mb-3">{item.title}</h3>
                <p className="text-sm text-[#94A3B8] font-space">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FAQ />
      <Contact />
    </div>
  );
};

export default ComputadorPage;
